import React, { useEffect, useMemo, useState } from 'react';
import {
  copyExternalAppPlaybook,
  executeExternalAppDestination,
  getExternalAppPlaybook,
  getExternalAppSourceOperation,
  listExternalAppPlaybookCopyTargets,
} from './externalAppControlApi';
import ListGeometryPanel from './ListGeometryPanel';
import ScreenStateTrainer from './ScreenStateTrainer';

const TERMINAL_OPERATION_STATUSES = new Set([
  'verified', 'completed_unverified', 'partial', 'needs_operator', 'failed',
  'expired', 'cancelled', 'superseded',
]);

const wait = (delayMs) => new Promise((resolve) => window.setTimeout(resolve, delayMs));

function reachableStates(playbook, startStateId) {
  const reached = new Set([startStateId]);
  let changed = true;
  while (changed) {
    changed = false;
    for (const transition of playbook.transitions || []) {
      if (!transition.fromStateIds?.some((stateId) => reached.has(stateId))) continue;
      for (const stateId of transition.toStateIds || []) {
        if (!reached.has(stateId)) {
          reached.add(stateId);
          changed = true;
        }
      }
    }
  }
  return reached;
}

function PlaybookSetupPanel({
  session,
  appLabel,
  disabled,
  controlTransport = 'google_tv',
  onRemoteAction,
  onMessage,
  onError,
}) {
  const [playbook, setPlaybook] = useState(null);
  const [busy, setBusy] = useState('loading');
  const [lastOperation, setLastOperation] = useState(null);
  const [copyTargets, setCopyTargets] = useState([]);
  const [selectedTargetIds, setSelectedTargetIds] = useState([]);

  const load = async () => {
    setBusy('loading');
    try {
      const [currentPlaybook, targets] = await Promise.all([
        getExternalAppPlaybook(session.id),
        listExternalAppPlaybookCopyTargets(session.profileId),
      ]);
      setPlaybook(currentPlaybook);
      setCopyTargets(targets);
      setSelectedTargetIds((current) => current.filter((deviceId) => (
        targets.some((target) => target.deviceId === deviceId && target.canCopy)
      )));
    } catch (error) {
      onError(error.response?.data?.error || error.message || 'Failed to load the app map.');
    } finally {
      setBusy('');
    }
  };

  const copyToDevices = async () => {
    setBusy('copy');
    try {
      const result = await copyExternalAppPlaybook(session.profileId, selectedTargetIds);
      const copiedCount = result?.copied?.length || 0;
      setSelectedTargetIds([]);
      onMessage(
        `Copied ${appLabel} setup to ${copiedCount} device${copiedCount === 1 ? '' : 's'}. `
        + 'Each copy is marked for visual revalidation.'
      );
      await load();
    } catch (error) {
      onError(error.response?.data?.error || error.message || 'Failed to copy the app setup.');
    } finally {
      setBusy('');
    }
  };

  const toggleCopyTarget = (deviceId) => {
    setSelectedTargetIds((current) => current.includes(deviceId)
      ? current.filter((candidate) => candidate !== deviceId)
      : [...current, deviceId]);
  };

  useEffect(() => {
    load();
  }, [session.id]);

  const coverage = useMemo(() => {
    if (!playbook) return [];
    return (playbook.states || []).map((state) => {
      const reached = reachableStates(playbook, state.id);
      return {
        state,
        destinations: (playbook.destinations || []).filter((destination) => (
          reached.has(destination.stateId)
        )),
      };
    });
  }, [playbook]);

  const testDestination = async (destination) => {
    setBusy(`destination:${destination.id}`);
    setLastOperation(null);
    try {
      const result = await executeExternalAppDestination(
        session.profileId,
        destination.id,
        { source: 'calibration' }
      );
      let operation = result?.operation || null;
      setLastOperation(operation);
      for (let check = 0; operation?.id && !TERMINAL_OPERATION_STATUSES.has(operation.status) && check < 180; check += 1) {
        await wait(1000);
        const latest = await getExternalAppSourceOperation(operation.id);
        operation = latest?.operation || operation;
        setLastOperation(operation);
      }
      const status = operation?.status || 'completed';
      onMessage(`${appLabel} destination test finished: ${status.replace(/_/g, ' ')}.`);
    } catch (error) {
      onError(error.response?.data?.error || error.message || 'Destination test failed.');
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="apps-launch-recipe">
      <div className="apps-launch-recipe__heading">
        <div>
          <span className="apps-setup-eyebrow">App navigation map</span>
          <h4>Teach meanings and reachable destinations</h4>
          <p>
            Capture recognizable screens below, then record routes between useful milestones.
            SuiteStream will observe the current screen and choose a safe route at runtime.
          </p>
        </div>
        <button type="button" onClick={load} disabled={disabled || Boolean(busy)}>
          {busy === 'loading' ? 'Reloading…' : 'Reload saved map'}
        </button>
      </div>

      {!playbook ? (
        <p>{busy ? 'Loading the current app map…' : 'No app map is available yet.'}</p>
      ) : (
        <>
          <ScreenStateTrainer
            session={session}
            playbook={playbook}
            controlTransport={controlTransport}
            onRemoteAction={onRemoteAction}
            onSaved={setPlaybook}
            onMessage={onMessage}
            onError={onError}
          />
          <div className="apps-launch-recipe__grid">
            <div>
              <strong>1. What screens are known?</strong>
              {(playbook.states || []).map((state) => (
                <p key={state.id}>
                  {state.label} · {state.kind.replace(/_/g, ' ')} ·{' '}
                  {state.recognisers?.length || 0} identifier(s)
                </p>
              ))}
            </div>
            <div>
              <strong>2. What happens next?</strong>
              {(playbook.transitions || []).length ? (playbook.transitions || []).map((transition) => (
                <p key={transition.id}>
                  {transition.label} · {transition.actions.map((action) => action.action).join(', ')}
                  {' '}→ {transition.toStateIds.join(' / ')}
                </p>
              )) : <p>No routes recorded yet. Use the route recorder above.</p>}
            </div>
          </div>

          <div>
            <strong>3. Can every known screen reach a destination?</strong>
            {coverage.map(({ state, destinations }) => (
              <p key={state.id}>
                {destinations.length ? '✓' : '!'} {state.label}:{' '}
                {destinations.length
                  ? destinations.map((destination) => destination.label).join(', ')
                  : 'no reachable destination yet'}
              </p>
            ))}
          </div>

          <div className="apps-launch-recipe__heading-actions">
            {(playbook.destinations || []).map((destination) => (
              <button
                key={destination.id}
                type="button"
                className="primary"
                onClick={() => testDestination(destination)}
                disabled={disabled || Boolean(busy)}
              >
                {busy === `destination:${destination.id}`
                  ? `Navigating to ${destination.label}…`
                  : `Test ${destination.label}`}
              </button>
            ))}
          </div>

          {lastOperation && (
            <div className={`apps-prompt-watch__status ${lastOperation.status}`}>
              <strong>{lastOperation.status.replace(/_/g, ' ')}</strong>
              <span>
                {lastOperation.result?.finalStateId || lastOperation.currentStateId || 'state unknown'}
                {' '}· {lastOperation.result?.keypresses || 0} keypress(es)
              </span>
              {lastOperation.errorMessage && <small>{lastOperation.errorMessage}</small>}
            </div>
          )}

          {copyTargets.length > 0 && (
            <div className="apps-launch-recipe__grid">
              <div>
                <strong>Reuse this setup on another device</strong>
                <p>
                  Devices in this area with the same app can share the navigation map.
                  Visual identifiers must be checked on the destination device afterward.
                </p>
              </div>
              <div>
                {copyTargets.map((target) => (
                  <label key={target.deviceId}>
                    <input
                      type="checkbox"
                      checked={selectedTargetIds.includes(target.deviceId)}
                      onChange={() => toggleCopyTarget(target.deviceId)}
                      disabled={disabled || Boolean(busy) || !target.canCopy}
                    />{' '}
                    {target.deviceName || `Device ${target.deviceId}`}
                    {target.resolution ? ` · ${target.resolution}` : ''}
                    {target.locale ? ` · ${target.locale}` : ''}
                    {!target.canCopy ? ' · enable and save this app first' : ''}
                  </label>
                ))}
                <button
                  type="button"
                  onClick={copyToDevices}
                  disabled={disabled || Boolean(busy) || selectedTargetIds.length === 0}
                >
                  {busy === 'copy' ? 'Copying…' : 'Copy setup'}
                </button>
              </div>
            </div>
          )}
          <ListGeometryPanel
            session={session}
            disabled={disabled || Boolean(busy)}
            onMessage={onMessage}
            onError={onError}
          />
        </>
      )}
    </div>
  );
}

export { reachableStates };
export default PlaybookSetupPanel;
